import React from 'react';
import { I18nManager, Platform, TouchableOpacity } from 'react-native';
import { useTranslation } from 'react-i18next';
import JSONTree from 'react-native-json-tree';

// DONT CHANGE IMPORT PATHS BELOW OR NEXT WILL STOP BUILDING ROUTES CORRECTLY
import { useTheme } from '@24i/nxg-sdk-higgs';
import { Text, View } from '@24i/nxg-sdk-quarks';
import { AppVersion } from '@24i/nxg-core-utils/src/globalSingletons';
import { WithWebHeaderProps } from '@24i/nxg-sdk-smartott/src/navigation/components/withWebHeader/types';
import { useAppConfigQuery } from '@24i/nxg-sdk-smartott-shared/src/hooks/query/useAppConfigQuery';
import { useStore } from '@24i/nxg-sdk-smartott/src/context/ApplicationStore';
import DeviceInfo from '@24i/nxg-sdk-quantum/src/DeviceInfo';

import SettingsWrapper from '../../SettingsScreen/components/SettingsWrapper';
import getTechnicalInfoScreenStyles from '../styles';
import { TECHNICAL_INFO_TEST_IDS } from '../types';

export interface TechnicalInfoScreenProps extends WithWebHeaderProps {
    title: string;
    isLandscape: boolean;
    isDetailOpened: boolean;
    onBack: null;
    setOpenedDetail: () => void;
    Sentry: any;
}

const Wrapper = SettingsWrapper;

const getDeviceVersion = (): string | undefined => {
    if (Platform.OS === 'web') {
        return undefined;
    }
    const deviceVersion = DeviceInfo.getVersion();
    // Only use DeviceInfo if it's a valid non-empty string that doesn't contain "undefined"
    if (deviceVersion && deviceVersion !== 'undefined' && !deviceVersion.includes('undefined')) {
        return deviceVersion;
    }
    return undefined;
};

const resolveAppVersion = (
    deviceVersion: string | undefined,
    realAppVersion: string | undefined,
    runtimeAppVersion: string | undefined,
    fallbackVersion: string
): string => {
    if (deviceVersion) {
        return deviceVersion;
    }
    if (realAppVersion && realAppVersion !== 'undefined') {
        return realAppVersion;
    }
    if (runtimeAppVersion && !runtimeAppVersion.includes('undefined')) {
        return runtimeAppVersion;
    }
    return fallbackVersion;
};

const resolveProductVersion = (
    runtimeProductVersion: string | undefined,
    fallbackVersion: string
): string => {
    if (runtimeProductVersion && runtimeProductVersion !== 'undefined') {
        return runtimeProductVersion;
    }
    return fallbackVersion;
};

export const TechnicalInfoScreen = ({
    title = '',
    isLandscape = false,
    isDetailOpened = false,
    onBack = null,
    setOpenedDetail = () => {},
    Sentry,
}: TechnicalInfoScreenProps) => {
    const { theme } = useTheme();
    const styles = getTechnicalInfoScreenStyles(theme);
    const { t } = useTranslation(['sott']);
    const { appSettings } = useAppConfigQuery();
    const { runtimeConfig } = useStore();

    const SHOW_DEV_INFO = __DEV__;
    const baseAppVersion = AppVersion.get();
    const deviceVersion = getDeviceVersion();
    const appVersionCode = Platform.OS !== 'web' ? DeviceInfo.getBuildNumber() : undefined;

    const {
        realAppVersion,
        appVersion: runtimeAppVersion,
        productVersion: runtimeProductVersion,
    } = runtimeConfig || {};

    const unknownVersion = t('settings.technicalInfo.unknown') as string;
    const appVersion = resolveAppVersion(
        deviceVersion || baseAppVersion,
        realAppVersion,
        runtimeAppVersion,
        unknownVersion
    );
    const productVersion = resolveProductVersion(runtimeProductVersion, unknownVersion);
    return (
        <Wrapper
            title={title}
            onBack={onBack}
            isDetailOpened={isDetailOpened}
            setOpenedDetail={setOpenedDetail}
        >
            <View style={{ width: '100%' }}>
                <View style={styles.itemWrapper}>
                    <Text
                        style={[styles.title, { textAlign: I18nManager.isRTL ? 'left' : 'auto' }]}
                    >
                        {t('settings.technicalInfo.appVersion')}
                    </Text>
                    <Text
                        style={[
                            styles.description,
                            isLandscape && styles.descriptionLandscape,
                            { textAlign: I18nManager.isRTL ? 'left' : 'auto' },
                        ]}
                        testID={TECHNICAL_INFO_TEST_IDS.APP_REALVERSION_TEXT}
                    >
                        {t('settings.technicalInfo.appVersion')}: {appVersion}
                        {appVersionCode ? ` (${appVersionCode})` : ''}
                    </Text>
                    <Text
                        style={[
                            styles.description,
                            isLandscape && styles.descriptionLandscape,
                            { textAlign: I18nManager.isRTL ? 'left' : 'auto' },
                        ]}
                        testID={TECHNICAL_INFO_TEST_IDS.APP_PRODUCTVERSION_TEXT}
                    >
                        Product: {productVersion}
                    </Text>
                </View>
                {SHOW_DEV_INFO && (
                    <View style={styles.itemWrapper}>
                        <Text style={styles.title}>App Settings</Text>
                        <JSONTree data={appSettings} />
                    </View>
                )}
                {SHOW_DEV_INFO && (
                    <View style={styles.itemWrapper}>
                        <Text style={styles.title}>Sentry</Text>
                        {/* @ts-ignore */}
                        <JSONTree data={Sentry.getCurrentHub().getClient()} />
                        <View style={{ flex: 1, flexDirection: 'row' }}>
                            <TouchableOpacity
                                onPress={() => {
                                    throw new Error('Sentry Test Error');
                                }}
                            >
                                <View
                                    style={{
                                        padding: 5,
                                        borderColor: 'white',
                                        borderWidth: 1,
                                        marginTop: 8,
                                        marginRight: 8,
                                    }}
                                >
                                    <Text style={{ color: 'white' }}>Crash JS</Text>
                                </View>
                            </TouchableOpacity>
                            {Platform.OS !== 'web' && (
                                <TouchableOpacity onPress={() => Sentry.nativeCrash()}>
                                    <View
                                        style={{
                                            padding: 5,
                                            borderColor: 'white',
                                            borderWidth: 1,
                                            marginTop: 8,
                                        }}
                                    >
                                        <Text style={{ color: 'white' }}>Crash Native</Text>
                                    </View>
                                </TouchableOpacity>
                            )}
                        </View>
                    </View>
                )}
            </View>
        </Wrapper>
    );
};
